import { z } from 'zod'
import { euroToCents } from '../sla'

export const lineupTypes = ['solo', 'duo', 'trio', 'quartett', 'band', 'streichquartett'] as const
export type LineupType = (typeof lineupTypes)[number]

export const lineupTypeLabels: Record<LineupType, string> = {
  solo: 'Solo',
  duo: 'Duo',
  trio: 'Trio',
  quartett: 'Quartett',
  band: 'Band (5+)',
  streichquartett: 'Streichquartett',
}

// the 100 EUR floor, the same number as the CHECK constraints on ensembles and ensemble_offers
export const PRICE_FLOOR_EURO = 100

const priceEuro = z.coerce
  .number({ message: 'Bitte gib einen Preis in Euro an.' })
  .int('Bitte gib den Preis in ganzen Euro an.')
  .min(PRICE_FLOOR_EURO, `Unter ${PRICE_FLOOR_EURO} € vermitteln wir keine Auftritte.`)
  .max(1_000_000)

const futureDate = z.iso
  .date('Bitte wähle ein Datum.')
  .refine(
    (value) => new Date(`${value}T12:00:00Z`).getTime() > Date.now(),
    'Das Datum liegt in der Vergangenheit.'
  )

const optionalUrl = z
  .union([
    z.literal(''),
    z.url({ protocol: /^https?$/, message: 'Bitte gib einen Link mit https:// an.' }),
  ])
  .optional()

// The form validates one step at a time, the API route validates all three together - the step
// objects are the single definition for both.
export const ensembleContactStep = z.object({
  ensembleName: z.string().trim().min(2, 'Wie heißt euer Ensemble?').max(120),
  contactName: z.string().trim().min(2, 'Bitte sag uns deinen Namen.').max(120),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email('Diese E-Mail-Adresse sieht nicht gültig aus.')),
  phone: z.string().trim().max(40).optional().or(z.literal('')),
  region: z.string().trim().min(2, 'Wo seid ihr zu Hause?').max(120),
})

export const gigOfferInput = z
  .object({
    dateFrom: futureDate,
    dateTo: futureDate,
    priceFromEuro: priceEuro,
    note: z.string().trim().max(1000).optional().or(z.literal('')),
  })
  .refine((v) => v.dateTo >= v.dateFrom, {
    message: 'Das Ende darf nicht vor dem Anfang liegen.',
    path: ['dateTo'],
  })
export type GigOfferInput = z.infer<typeof gigOfferInput>

export const ensembleInfoStep = z.object({
  lineupType: z.enum(lineupTypes, { message: 'Bitte wähle eure Besetzung.' }),
  genres: z.string().trim().max(200).optional().or(z.literal('')),
  priceFromEuro: priceEuro,
  videoUrl: optionalUrl,
  websiteUrl: optionalUrl,
  consent: z.literal(true, {
    message: 'Ohne deine Einwilligung dürfen wir die Anmeldung nicht speichern.',
  }),
  // honeypot, same contract as the booking request: accepted, then silently ignored by the route
  website: z.string().max(400).optional(),
})

export const ensembleSignupSchema = ensembleContactStep.extend(ensembleInfoStep.shape).extend({
  gig: gigOfferInput.optional(),
})
export type EnsembleSignupInput = z.infer<typeof ensembleSignupSchema>

/** "Pop, Soul , Jazz" -> ['pop', 'soul', 'jazz'], de-duplicated and capped at eight. */
export function parseGenres(value?: string) {
  const genres = (value ?? '')
    .split(',')
    .map((genre) => genre.trim().toLowerCase())
    .filter((genre) => genre.length > 0 && genre.length <= 40)
  return [...new Set(genres)].slice(0, 8)
}

/** URL slug from the ensemble name; the route appends a random suffix so two ensembles never collide. */
export function slugify(name: string) {
  return (
    name
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .normalize('NFKD')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'ensemble'
  )
}

/** The ensemble row and its optional first offer, money in cents. Status and slug are set server-side. */
export function toEnsembleRows(input: EnsembleSignupInput, createdAt = new Date()) {
  const blank = (value?: string) => (value && value.length > 0 ? value : null)
  const ensemble = {
    name: input.ensembleName,
    contactName: input.contactName,
    email: input.email,
    phone: blank(input.phone),
    region: input.region,
    lineupType: input.lineupType,
    genres: parseGenres(input.genres),
    priceFromCents: euroToCents(input.priceFromEuro),
    videoUrl: blank(input.videoUrl),
    websiteUrl: blank(input.websiteUrl),
    consentAt: createdAt,
  }
  const offer = input.gig
    ? {
        region: input.region,
        dateFrom: new Date(`${input.gig.dateFrom}T00:00:00Z`),
        dateTo: new Date(`${input.gig.dateTo}T23:59:59Z`),
        priceFromCents: euroToCents(input.gig.priceFromEuro),
        note: blank(input.gig.note),
      }
    : null
  return { ensemble, offer }
}

/** A further gig offer of a logged-in ensemble: the region is the ensemble's own, so it is not asked again. */
export function toEnsembleOfferRow(input: GigOfferInput) {
  const note = input.note && input.note.length > 0 ? input.note : null
  return {
    dateFrom: new Date(`${input.dateFrom}T00:00:00Z`),
    dateTo: new Date(`${input.dateTo}T23:59:59Z`),
    priceFromCents: euroToCents(input.priceFromEuro),
    note,
  }
}
